import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { X, Calendar, Clock, Dog, Scissors, User, CreditCard } from 'lucide-react';
import { appointments as appointmentsApi, services as servicesApi, groomers as groomersApi, sizeCategories as sizeCategoriesApi } from '../utils/api';
import axios from 'axios';
import AddOnsSelector from './AddOnsSelector';
import usePaymentSettings from '../hooks/usePaymentSettings';
import useBookingFormLabels from '../hooks/useBookingFormLabels';
import useSubjectSettings from '../hooks/useSubjectSettings';
import StripePaymentModal from './StripePaymentModal';
import PayPalPaymentModal from './PayPalPaymentModal';
import SquarePaymentModal from './SquarePaymentModal';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const BookForClientModal = ({ client, clientDetails, onClose, onSuccess }) => {
  const { canProcessPayments, isPaymentRequired, allowsPartialPayment, depositPercentage, providers } = usePaymentSettings();
  const { labels } = useBookingFormLabels();
  const { isEnabled: subjectsEnabled, isRequired: subjectRequired, singularName: subjectSingularName } = useSubjectSettings();
  const [formData, setFormData] = useState({
    petId: '',
    serviceId: '',
    groomerId: '',
    appointmentDate: '',
    startTime: '',
    specialRequests: '',
    petSize: '', // Override size
  });
  const [services, setServices] = useState([]);
  const [groomers, setGroomers] = useState([]);
  const [sizeCategories, setSizeCategories] = useState([]);
  const [servicesEnabled, setServicesEnabled] = useState(true);
  const [addonsEnabled, setAddonsEnabled] = useState(true);
  const [flatRatePrice, setFlatRatePrice] = useState(0);
  const [allowSizeOverride, setAllowSizeOverride] = useState(false);
  const [allowEmployeeSelection, setAllowEmployeeSelection] = useState(true);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentOption, setPaymentOption] = useState('later'); // 'full', 'deposit', or 'later'
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(''); // Selected provider
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [showPayPalModal, setShowPayPalModal] = useState(false);
  const [showSquareModal, setShowSquareModal] = useState(false);
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);

  useEffect(() => {
    loadServices();
    loadGroomers();
    loadSizeCategories();
    loadBookingFeatures();
    loadSizeSettings();
    loadAppointmentSettings();
  }, []);

  useEffect(() => {
    // Listen for booking features updates
    const handleUpdate = () => {
      loadBookingFeatures();
    };
    window.addEventListener('bookingFeaturesUpdated', handleUpdate);
    return () => {
      window.removeEventListener('bookingFeaturesUpdated', handleUpdate);
    };
  }, []);

  // Set default payment method when providers load
  useEffect(() => {
    if (providers.length > 0 && !selectedPaymentMethod) {
      setSelectedPaymentMethod(providers[0].provider);
    }
  }, [providers, selectedPaymentMethod]);

  // Set default payment option to full if payment is required
  useEffect(() => {
    if (isPaymentRequired && paymentOption === 'later') {
      setPaymentOption('full');
    }
  }, [isPaymentRequired, paymentOption]);

  const loadServices = async () => {
    try {
      const response = await servicesApi.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Load services error:', error);
    }
  };

  const loadGroomers = async () => {
    try {
      const response = await groomersApi.getAll();
      setGroomers(response.data);
    } catch (error) {
      console.error('Load groomers error:', error);
    }
  };

  const loadSizeCategories = async () => {
    try {
      const response = await sizeCategoriesApi.getAll();
      setSizeCategories(response.data);
    } catch (error) {
      console.error('Load size categories error:', error);
    }
  };

  const loadBookingFeatures = async () => {
    try {
      const [servicesRes, addonsRes, flatRateRes] = await Promise.all([
        axios.get(`${API_URL}/business-info/services_enabled`).catch(() => ({ data: { info_value: 'true' } })),
        axios.get(`${API_URL}/business-info/addons_enabled`).catch(() => ({ data: { info_value: 'true' } })),
        axios.get(`${API_URL}/business-info/flat_rate_price`).catch(() => ({ data: { info_value: '0' } })),
      ]);
      setServicesEnabled(servicesRes.data.info_value !== 'false');
      setAddonsEnabled(addonsRes.data.info_value !== 'false');
      setFlatRatePrice(parseFloat(flatRateRes.data.info_value) || 0);
    } catch (error) {
      console.error('Load booking features error:', error);
    }
  };

  const loadSizeSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/size-categories/settings`);
      setAllowSizeOverride(response.data.allow_size_override === 'true');
    } catch (error) {
      console.error('Load size settings error:', error);
    }
  };

  const loadAppointmentSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/business-info/allow_employee_selection`).catch(() => ({ data: { info_value: 'true' } }));
      setAllowEmployeeSelection(response.data.info_value !== 'false');
    } catch (error) {
      console.error('Load appointment settings error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Set default size when pet is selected
    if (name === 'petId') {
      const selectedPet = clientDetails.pets?.find(p => p.id === value);
      if (selectedPet) {
        setFormData(prev => ({ ...prev, petSize: selectedPet.size || '' }));
      }
    }
    
    // Reset time slot when date, service, pet, or groomer changes
    if (name === 'appointmentDate' || name === 'serviceId' || name === 'petId' || name === 'groomerId' || name === 'petSize') {
      setFormData(prev => ({ ...prev, startTime: '' }));
      setAvailableSlots([]);
    }
  };

  // Load available slots when date, service, pet, and groomer are selected
  useEffect(() => {
    const loadAvailableSlots = async () => {
      if (!formData.appointmentDate || (servicesEnabled && !formData.serviceId) || (subjectsEnabled && subjectRequired && !formData.petId)) {
        setAvailableSlots([]);
        return;
      }

      const selectedPet = formData.petId ? clientDetails.pets?.find(p => p.id === formData.petId) : null;

      setLoadingSlots(true);
      try {
        const response = await appointmentsApi.getAvailableSlots({
          date: formData.appointmentDate,
          serviceId: formData.serviceId || undefined,
          petSize: formData.petSize || selectedPet?.size || 'standard',
          groomerId: formData.groomerId || undefined,
        });
        setAvailableSlots(response.data);
      } catch (error) {
        console.error('Load available slots error:', error);
        toast.error('Failed to load available time slots');
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    loadAvailableSlots();
  }, [formData.appointmentDate, formData.serviceId, formData.petId, formData.petSize, formData.groomerId, clientDetails.pets, servicesEnabled, subjectsEnabled, subjectRequired]);

  const getTotalPrice = () => {
    let basePrice = 0;
    if (servicesEnabled) {
      const selectedService = services.find(s => s.id === formData.serviceId);
      if (selectedService) {
        const sizeToUse = formData.petSize || 'standard';
        const servicePricing = selectedService.pricing?.find(p => p.size?.toLowerCase() === sizeToUse.toLowerCase()) 
                              || selectedService.pricing?.[0];
        basePrice = parseFloat(servicePricing?.price) || 0;
      }
    } else {
      basePrice = flatRatePrice;
    }
    const addonsPrice = selectedAddons.reduce((sum, addon) => sum + (parseFloat(addon.price) * (addon.quantity || 1)), 0);
    return basePrice + addonsPrice;
  };

  const getPaymentAmount = () => {
    const total = getTotalPrice();
    if (paymentOption === 'deposit' && allowsPartialPayment) {
      return (total * depositPercentage / 100).toFixed(2);
    }
    return total.toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const appointmentData = {
      ...formData,
      petId: formData.petId || null,
      serviceId: formData.serviceId || null,
      clientId: client.id,
      addons: selectedAddons.map(addon => ({
        addonId: addon.id,
        quantity: addon.quantity || 1,
        price: addon.price,
      })),
    };

    // Check if we need to process payment with Stripe
    if (canProcessPayments && paymentOption !== 'later' && selectedPaymentMethod === 'stripe') {
      // Show Stripe payment modal
      setPendingAppointmentData(appointmentData);
      setShowStripeModal(true);
      return;
    }

    // Check if we need to process payment with PayPal
    if (canProcessPayments && paymentOption !== 'later' && selectedPaymentMethod === 'paypal') {
      // Show PayPal payment modal
      setPendingAppointmentData(appointmentData);
      setShowPayPalModal(true);
      return;
    }

    // Check if we need to process payment with Square
    if (canProcessPayments && paymentOption !== 'later' && selectedPaymentMethod === 'square') {
      // Show Square payment modal
      setPendingAppointmentData(appointmentData);
      setShowSquareModal(true);
      return;
    }

    // For other payment methods or no payment, create appointment directly
    await createAppointment(appointmentData);
  };

  const createAppointment = async (appointmentData) => {
    setSubmitting(true);

    try {
      // Add payment information if payment is being processed (not Pay in Person)
      if (canProcessPayments && paymentOption !== 'later' && selectedPaymentMethod !== 'cash_only') {
        appointmentData.paymentStatus = paymentOption === 'full' ? 'paid' : 'partial';
        appointmentData.paymentMethod = selectedPaymentMethod || providers[0]?.provider;
        appointmentData.amountPaid = getPaymentAmount();
        appointmentData.paymentTransactionId = `${(selectedPaymentMethod || providers[0]?.provider).toUpperCase()}_${Date.now()}`;
      }

      const createdRes = await appointmentsApi.create(appointmentData);
      const pendingForms = createdRes?.data?.pending_forms || [];

      if (pendingForms.length > 0) {
        toast.success(
          `Appointment booked. ${pendingForms.length} intake form${pendingForms.length === 1 ? '' : 's'} auto-assigned — the client will see the link(s) on their appointments page.`,
          { autoClose: 7000 }
        );
      } else {
        toast.success('Appointment booked successfully!');
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Book appointment error:', error);
      toast.error(error.response?.data?.error || 'Failed to book appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripePaymentSuccess = async () => {
    // Payment successful, now create the appointment with payment info
    if (pendingAppointmentData) {
      pendingAppointmentData.paymentStatus = paymentOption === 'full' ? 'paid' : 'partial';
      pendingAppointmentData.paymentMethod = 'stripe';
      pendingAppointmentData.amountPaid = getPaymentAmount();
      // The payment intent ID will be set by the backend
      await createAppointment(pendingAppointmentData);
    }
  };

  const handlePayPalPaymentSuccess = async (captureId) => {
    // Payment successful, now create the appointment with payment info
    if (pendingAppointmentData) {
      pendingAppointmentData.paymentStatus = paymentOption === 'full' ? 'paid' : 'partial';
      pendingAppointmentData.paymentMethod = 'paypal';
      pendingAppointmentData.amountPaid = getPaymentAmount();
      pendingAppointmentData.paymentTransactionId = captureId;
      await createAppointment(pendingAppointmentData);
    }
  };

  const handleSquarePaymentSuccess = async (paymentId) => {
    // Payment successful, now create the appointment with payment info
    if (pendingAppointmentData) {
      pendingAppointmentData.paymentStatus = paymentOption === 'full' ? 'paid' : 'partial';
      pendingAppointmentData.paymentMethod = 'square';
      pendingAppointmentData.amountPaid = getPaymentAmount();
      pendingAppointmentData.paymentTransactionId = paymentId;
      await createAppointment(pendingAppointmentData);
    }
  };

  const selectedService = services.find(s => s.id === formData.serviceId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{labels.modalTitle}</h2>
            <p className="text-sm text-gray-600 mt-1">
              For {client.first_name} {client.last_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Select Pet - Only show if subjects are enabled */}
          {subjectsEnabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {labels.selectSubjectLabel} {subjectRequired && '*'}
              </label>
              {clientDetails.pets && clientDetails.pets.length > 0 ? (
                <div className="relative">
                  <Dog className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <select
                    name="petId"
                    value={formData.petId}
                    onChange={handleChange}
                    required={subjectRequired}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{labels.selectSubjectPlaceholder}</option>
                    {clientDetails.pets.map(pet => (
                      <option key={pet.id} value={pet.id}>
                        {pet.name} - {pet.breed} ({pet.size})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    This client doesn't have any {subjectSingularName.toLowerCase()}s yet. Please add a {subjectSingularName.toLowerCase()} first.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Size Selector - Only show if override is allowed */}
          {allowSizeOverride && formData.petId && sizeCategories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size Category *
              </label>
              <select
                name="petSize"
                value={formData.petSize}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select size...</option>
                {sizeCategories.map(category => (
                  <option key={category.id} value={category.category_name}>
                    {category.category_name} - {category.description}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Adjust if the pet's size has changed or is incorrect
              </p>
            </div>
          )}

          {/* Select Service */}
          {servicesEnabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {labels.selectServiceLabel} *
              </label>
            <div className="relative">
              <Scissors className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <select
                name="serviceId"
                value={formData.serviceId}
                onChange={handleChange}
                required={servicesEnabled}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">{labels.selectServicePlaceholder}</option>
                {services.map(service => {
                  // Use the selected/overridden size, or 'standard' if sizing is disabled
                  const petSize = formData.petSize || 'standard';
                  
                  // Find exact pricing for this size
                  const sizePricing = service.pricing?.find(p => p.size?.toLowerCase() === petSize.toLowerCase())
                                   || service.pricing?.[0]; // Fallback to first pricing
                  
                  let priceDisplay, durationDisplay;
                  
                  if (sizePricing) {
                    // Show exact price for this pet's size
                    priceDisplay = `$${parseFloat(sizePricing.price).toFixed(2)}`;
                    durationDisplay = `${sizePricing.durationMinutes} min`;
                  } else {
                    // Fallback to price range if no pricing found
                    const prices = service.pricing?.map(p => parseFloat(p.price)).filter(p => !isNaN(p)) || [];
                    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                    priceDisplay = minPrice === maxPrice 
                      ? `$${minPrice.toFixed(2)}` 
                      : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
                    
                    const durations = service.pricing?.map(p => parseInt(p.durationMinutes)).filter(d => !isNaN(d)) || [];
                    const minDuration = durations.length > 0 ? Math.min(...durations) : 0;
                    const maxDuration = durations.length > 0 ? Math.max(...durations) : 0;
                    durationDisplay = minDuration === maxDuration 
                      ? `${minDuration} min` 
                      : `${minDuration}-${maxDuration} min`;
                  }
                  
                  return (
                    <option key={service.id} value={service.id}>
                      {service.name} - {priceDisplay} ({durationDisplay})
                    </option>
                  );
                })}
              </select>
            </div>
            {selectedService && (
              <p className="text-sm text-gray-600 mt-1">
                {selectedService.description}
              </p>
            )}
            </div>
          )}

          {/* Select Groomer - Only show if employee selection is allowed */}
          {allowEmployeeSelection && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {labels.selectGroomerLabel} {labels.selectGroomerOptional}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <select
                  name="groomerId"
                  value={formData.groomerId}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">{labels.selectGroomerPlaceholder}</option>
                  {groomers.map(groomer => (
                    <option key={groomer.id} value={groomer.id}>
                      {groomer.first_name} {groomer.last_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {labels.dateLabel} *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="date"
                name="appointmentDate"
                value={formData.appointmentDate}
                onChange={handleChange}
                required
                min={new Date().toISOString().split('T')[0]}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Available Time Slots */}
          {formData.appointmentDate && (!servicesEnabled || formData.serviceId) && (!(subjectsEnabled && subjectRequired) || formData.petId) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {labels.timeLabel} *
              </label>
              {loadingSlots ? (
                <div className="text-center py-4 text-gray-600">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                  <p className="text-sm">{labels.loadingTimesMessage}</p>
                </div>
              ) : availableSlots.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                  {availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, startTime: slot.startTime }))}
                      className={`p-2 rounded-lg border-2 transition-colors ${
                        formData.startTime === slot.startTime
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-primary-400'
                      }`}
                    >
                      <div className="flex items-center justify-center text-sm font-medium">
                        <Clock size={14} className="mr-1" />
                        {slot.startTime.substring(0, 5)}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    {labels.noTimesMessage}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Flat Rate Display - when services disabled */}
          {!servicesEnabled && flatRatePrice > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Base Appointment Rate:</span>
                <span className="font-bold text-primary-600">${flatRatePrice.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Add-ons */}
          {addonsEnabled && (
            <div>
              <AddOnsSelector
                selectedAddons={selectedAddons}
                onAddonsChange={setSelectedAddons}
                label={labels.addExtrasLabel}
              />
            </div>
          )}

          {/* Special Requests */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {labels.specialRequestsLabel}
            </label>
            <textarea
              name="specialRequests"
              value={formData.specialRequests}
              onChange={handleChange}
              rows={3}
              placeholder={labels.specialRequestsPlaceholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Payment Options */}
          {canProcessPayments && (!servicesEnabled || formData.serviceId) && (!(subjectsEnabled && subjectRequired) || formData.petId) && (
            <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg space-y-3">
              <div className="flex items-center space-x-2">
                <CreditCard className="text-green-600" size={20} />
                <h3 className="font-semibold text-gray-900">Payment Options</h3>
              </div>
              
              <div className="text-sm text-gray-700 mb-2">
                Total: <span className="font-bold text-lg">${getTotalPrice().toFixed(2)}</span>
              </div>

              {/* Payment Method Selection */}
              {providers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={selectedPaymentMethod}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    {/* Deduplicate providers by provider name */}
                    {Array.from(new Set(providers.map(p => p.provider)))
                      .map(providerName => providers.find(p => p.provider === providerName))
                      .map((prov) => (
                        <option key={prov.provider} value={prov.provider}>
                          {prov.provider === 'stripe' && 'Stripe (Credit/Debit Card)'}
                          {prov.provider === 'paypal' && 'PayPal'}
                          {prov.provider === 'square' && 'Square'}
                          {prov.provider === 'cash_only' && 'Pay in Person'}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Only show payment processing options if NOT Pay in Person */}
              {selectedPaymentMethod !== 'cash_only' && (
                <>
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:border-green-600 transition-colors">
                      <input
                        type="radio"
                        name="paymentOption"
                        value="full"
                        checked={paymentOption === 'full'}
                        onChange={(e) => setPaymentOption(e.target.value)}
                        className="w-4 h-4 text-green-600"
                      />
                      <div className="ml-3">
                        <div className="font-medium">Process Full Payment</div>
                        <div className="text-xs text-gray-600">
                          Charge ${getTotalPrice().toFixed(2)}
                        </div>
                      </div>
                    </label>

                    {allowsPartialPayment && (
                      <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:border-green-600 transition-colors">
                        <input
                          type="radio"
                          name="paymentOption"
                          value="deposit"
                          checked={paymentOption === 'deposit'}
                          onChange={(e) => setPaymentOption(e.target.value)}
                          className="w-4 h-4 text-green-600"
                        />
                        <div className="ml-3">
                          <div className="font-medium">Process Deposit ({depositPercentage}%)</div>
                          <div className="text-xs text-gray-600">
                            Charge ${(getTotalPrice() * depositPercentage / 100).toFixed(2)} now
                          </div>
                        </div>
                      </label>
                    )}
                  </div>

                </>
              )}

              {/* Show message when Pay in Person is selected */}
              {selectedPaymentMethod === 'cash_only' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                  <strong>Pay in Person:</strong> Client will pay when they arrive for their appointment.
                </div>
              )}
            </div>
          )}

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This appointment will be created with "pending" status 
              and will need to be approved by an administrator.
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (subjectsEnabled && subjectRequired && (!clientDetails.pets || clientDetails.pets.length === 0))}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? labels.submittingButton : labels.submitButton}
            </button>
          </div>
        </form>
      </div>

      {/* Stripe Payment Modal */}
      <StripePaymentModal
        isOpen={showStripeModal}
        onClose={() => setShowStripeModal(false)}
        amount={parseFloat(getPaymentAmount())}
        onSuccess={handleStripePaymentSuccess}
        appointmentId={null}
      />

      {/* PayPal Payment Modal */}
      <PayPalPaymentModal
        isOpen={showPayPalModal}
        onClose={() => setShowPayPalModal(false)}
        amount={parseFloat(getPaymentAmount())}
        onSuccess={handlePayPalPaymentSuccess}
        appointmentId={null}
      />

      {/* Square Payment Modal */}
      <SquarePaymentModal
        isOpen={showSquareModal}
        onClose={() => setShowSquareModal(false)}
        amount={parseFloat(getPaymentAmount())}
        onSuccess={handleSquarePaymentSuccess}
        appointmentId={null}
      />
    </div>
  );
};

export default BookForClientModal;
